import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CarsProvider } from './context/CarsContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Payment from './pages/Payment';
import Shipping from './pages/Shipping';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import AdminShell from './components/AdminLayout';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

/** Outer wrapper for all `/admin/*` routes: full-height, no public header/footer. */
const AdminRoot: React.FC = () => (
  <div className="admin-app">
    <Outlet />
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/payment" element={<PublicLayout><Payment /></PublicLayout>} />
      <Route path="/shipping" element={<PublicLayout><Shipping /></PublicLayout>} />

      <Route path="/admin" element={<AdminRoot />}>
        <Route index element={<AdminLogin />} />
        <Route
          element={(
            <ProtectedRoute>
              <AdminShell />
            </ProtectedRoute>
          )}
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  return (
    <div className={`App${isAdmin ? ' App--admin' : ''}`}>
      <AppRoutes />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SiteSettingsProvider>
          <CarsProvider>
            <AppShell />
          </CarsProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
